/*
 * -------------------------------------------------------
 * Document Title
 * -------------------------------------------------------
 *
 * Author      : Vikram Singh
 * Created On  : Jun 2026
 *
 * Description:
 * ------------
 * Handles document title management and updates.
 */

import type { RouteLocationNormalized } from 'vue-router'

/**
 * The brand name to be displayed in the document title.
 */
export const DOCUMENT_TITLE_BRAND = 'Maropost'

type DocumentTitleWindow = Window & {
  __maropostSetPageTitle?: (pageTitle: string) => void
  __maropostSetPageTitleFromRoute?: (route: DocumentTitleRouteInput) => void
  __maropostGetDocumentTitleBrand?: () => string
  __maropostFormatDocumentTitle?: (pageTitle: string) => string
}

export type DocumentTitleRouteInput = Pick<
  RouteLocationNormalized,
  'name' | 'matched'
>

let currentPageTitle = ''
let unreadCount = 0

/**
 * Formats the document title.
 * Eg: `(3) Maropost - Dashboard` when unread count > 0, otherwise `Maropost - Dashboard`.
 */
export const formatDocumentTitle = (
  pageTitle: string,
  unread: number = unreadCount,
): string => {
  const base = pageTitle.trim()
    ? `${DOCUMENT_TITLE_BRAND} - ${pageTitle.trim()}`
    : DOCUMENT_TITLE_BRAND

  return unread > 0 ? `(${unread.toLocaleString()}) ${base}` : base
}

/**
 * Converts a route name into a human-readable page title segment.
 */
export const formatRouteName = (name: string): string => {
  return name
    .replace(/(Index|Page|IndexPage)$/i, '')
    .replace(/[-_]/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

/**
 * Resolves the page title segment from route meta or route name.
 */
export const resolvePageTitleFromRoute = (
  to: DocumentTitleRouteInput,
): string => {
  const matched = [...(to.matched ?? [])].reverse()

  for (const record of matched) {
    const metaTitle = (record.meta as { title?: unknown } | undefined)?.title
    if (typeof metaTitle === 'string' && metaTitle.trim()) {
      return metaTitle.trim()
    }
  }

  const routeName = to.name
  if (typeof routeName === 'string' && routeName.trim()) {
    return formatRouteName(routeName)
  }

  return ''
}

/**
 * Sets the current page title and applies the unread notification prefix.
 */
export const setDocumentPageTitle = (pageTitle: string): void => {
  const nextTitle = pageTitle.trim()

  // Embedded widget routers may call the bridge with an empty title — keep the parent page title.
  if (!nextTitle && currentPageTitle.trim()) {
    document.title = formatDocumentTitle(currentPageTitle, unreadCount)
    return
  }

  currentPageTitle = nextTitle || currentPageTitle
  document.title = formatDocumentTitle(currentPageTitle, unreadCount)
}

/**
 * Resolves and sets the page title from a route object.
 */
export const setDocumentPageTitleFromRoute = (
  to: DocumentTitleRouteInput,
): void => {
  setDocumentPageTitle(resolvePageTitleFromRoute(to))
}

/**
 * Updates unread count and refreshes the document title.
 */
export const setDocumentUnreadCount = (count: number): void => {
  unreadCount = Math.max(0, Number(count) || 0)
  document.title = formatDocumentTitle(currentPageTitle, unreadCount)
}

export const getDocumentUnreadCount = (): number => unreadCount

/**
 * Exposes document title helpers to MFE remotes
 */
export const registerDocumentTitleBridge = (): void => {
  const w = window as DocumentTitleWindow
  w.__maropostSetPageTitle = setDocumentPageTitle
  w.__maropostSetPageTitleFromRoute = setDocumentPageTitleFromRoute
  w.__maropostGetDocumentTitleBrand = () => DOCUMENT_TITLE_BRAND
  w.__maropostFormatDocumentTitle = (pageTitle: string) => formatDocumentTitle(pageTitle, unreadCount)
}
